'use client'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { SERVICE_TYPE_LABEL, SERVICE_TYPE_TO_FLOW, type FlowType } from '@/features/cases'
import { useMyInvestorCases } from '@/features/investors'
import { ServiceUpsell } from '@/features/investor/components/home/service-upsell'
import { InvestorShell } from '@/features/investor/components/investor-shell'
import { SERVICE_META } from '@/features/investor/components/home/service-meta'
import { useMyRecommendations, useUpdateRecommendationStatus } from '@/features/recommendations'
import { queryKeys } from '@/lib/queryKeys'

const TOTAL_SERVICES = Object.keys(SERVICE_META).length

/** Ported from `nivex-design/apps/nivex-web-v3/src/pages/dashboard/DashboardExplore.tsx`. */
export function InvestorExploreView() {
  const { data: investorCases = [] } = useMyInvestorCases()
  const { data: recommendations = [] } = useMyRecommendations()
  const updateRecommendationStatus = useUpdateRecommendationStatus(queryKeys.recommendations.mine)
  const sortedRecommendations = [...recommendations].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  )
  const ownedFlows = new Set<FlowType>(
    investorCases.map((c) => SERVICE_TYPE_TO_FLOW[c.serviceType]),
  )
  const allOwned = ownedFlows.size >= TOTAL_SERVICES

  return (
    <InvestorShell
      active="home"
      title="Explore services"
      description="The other ways Nivex can help, beyond what you've already started."
    >
      {sortedRecommendations.length > 0 && (
        <Card className="mb-4">
          <CardContent className="flex flex-col gap-4">
            <p className="text-sm font-medium">Recommended for you</p>
            <div className="flex flex-col gap-3">
              {sortedRecommendations.map((rec) => (
                <div
                  key={rec.id}
                  className="flex flex-col gap-2 border-b pb-3 last:border-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold">{SERVICE_TYPE_LABEL[rec.serviceType]}</p>
                      {rec.status !== 'open' && (
                        <Badge variant={rec.status === 'accepted' ? 'secondary' : 'outline'}>
                          {rec.status === 'accepted' ? 'Accepted' : 'Dismissed'}
                        </Badge>
                      )}
                    </div>
                    <p className="text-muted-foreground text-xs">{rec.reason}</p>
                    <p className="text-muted-foreground text-xs">
                      {rec.recommendedByName ? `${rec.recommendedByName} · ` : ''}
                      {new Date(rec.createdAt).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </p>
                  </div>
                  {rec.status === 'open' && (
                    <div className="flex shrink-0 items-center gap-1.5">
                      <Button
                        size="sm"
                        onClick={() =>
                          updateRecommendationStatus.mutate({ id: rec.id, status: 'accepted' })
                        }
                      >
                        Accept
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          updateRecommendationStatus.mutate({ id: rec.id, status: 'dismissed' })
                        }
                      >
                        Dismiss
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {allOwned ? (
        <Card>
          <CardContent className="text-muted-foreground py-8 text-center text-sm">
            You&apos;re already using every service — nothing left to explore.
          </CardContent>
        </Card>
      ) : (
        <ServiceUpsell ownedFlows={ownedFlows} />
      )}
    </InvestorShell>
  )
}
